import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import type { FormikProps } from "formik"
import type { QueryClient } from "@tanstack/react-query"
import * as Yup from "yup"
import { enqueueSnackbar } from "notistack"
import { Link, Skeleton, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { MERCHANT_ADMIN_ROUTE, MERCHANT_ADMIN_SETTINGS_MENU_PLANNING_ROUTE } from "../../../../../../constants/routes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { StandardNoResultText } from "../../../../../../shared/components/noResult/StandardNoResultText"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { CreateOrderCloseConfirmDialog } from "../../../../../../shared/components/createOrder/components/CreateOrderCloseConfirmDialog"
import { StandardFormikErrorScrollWrapper } from "../../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { fetchAllergens } from "../../../../admin/products/productsApi"

import { createMenuTemplate, fetchMenuTemplate, MenuTemplatesQuery, updateMenuTemplate } from "../menuTemplatesApi"
import {
    buildDishValidationSchema,
    defaultDishFormValues,
    emptyTemplateFormValues,
    toTemplateFormValues,
    toTemplatePayload,
} from "../helpers"
import { DishFormValues, MenuTemplateFormValues } from "../menuTemplatesTypes"
import { CategoryNameDialog } from "../components/CategoryNameDialog"
import { DishEditor, FormSubmitState } from "../components/DishEditor"
import { TemplateStructureForm } from "../components/TemplateStructureForm"

const useStyles = makeStyles()(() => ({
    sectionSkeleton: {
        height: "10rem",
        borderRadius: ".625rem",
    },
    cardSkeleton: {
        height: "4rem",
        borderRadius: ".625rem",
    },
    templateCrumbLink: {
        cursor: "pointer",
    },
}))

const SKELETON_CARDS = 3

/** dishIndex null = adding a new dish; the drill-in commits into the template form only via Done. */
type EditingDish = Readonly<{
    categoryIndex: number
    dishIndex: number | null
    initialValues: DishFormValues
}>

type CategoryDialogState = Readonly<{
    open: boolean
    categoryIndex: number | null
}>

/** Deleting a category or a dish is confirmed first; neither can be undone within the form. */
type DeleteTarget =
    | Readonly<{ kind: "category"; categoryIndex: number }>
    | Readonly<{ kind: "dish"; categoryIndex: number; dishIndex: number }>

export function MenuTemplateEditor() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { merchantId } = useMerchant()
    const { templateId } = useParams()
    const isEdit = templateId !== undefined

    const [editingDish, setEditingDish] = useState<EditingDish | null>(null)
    const [isDiscardConfirmOpen, setDiscardConfirmOpen] = useState(false)
    const [categoryDialog, setCategoryDialog] = useState<CategoryDialogState>({ open: false, categoryIndex: null })
    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
    const dishFormRef = useRef<FormikProps<DishFormValues>>(null)
    const templateFormRef = useRef<FormikProps<MenuTemplateFormValues>>(null)
    // Both Done and Save template live in the page header, outside the Formik they submit, so each
    // Formik's disabled state (per the project's submit-button rule) is mirrored here via onStateChange.
    const [dishFormState, setDishFormState] = useState<FormSubmitState>({ isValid: true, submitCount: 0 })
    const [templateFormState, setTemplateFormState] = useState<FormSubmitState>({ isValid: true, submitCount: 0 })
    const handleDishFormStateChange = useCallback((state: FormSubmitState) => setDishFormState(state), [])

    // Cancel and the template breadcrumb discard the drill-in's edits — confirmed first when dirty.
    const requestCloseDishEditor = () => {
        if (dishFormRef.current?.dirty) {
            setDiscardConfirmOpen(true)
            return
        }
        setEditingDish(null)
    }

    const handleDiscardDish = () => {
        setDiscardConfirmOpen(false)
        setEditingDish(null)
    }

    const allergensQuery = useApiQuery(fetchAllergens, {
        queryName: "allergens",
        dependencies: {},
        select: (data) => data.allergens,
    })

    const detailsQuery = useApiQuery(fetchMenuTemplate, {
        queryName: MenuTemplatesQuery.DETAILS,
        dependencies: { templateId: templateId ?? "" },
        enabled: isEdit,
        staleTime: 0,
    })

    const invalidateTemplates = async ({ queryClient }: { queryClient: QueryClient }) => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: [MenuTemplatesQuery.LIST] }),
            queryClient.invalidateQueries({ queryKey: [MenuTemplatesQuery.DETAILS] }),
        ])
    }

    const { mutateAsync: callCreateTemplate, isLoading: isCreatingTemplate } = useApiMutation(createMenuTemplate, {
        onSuccess: (_params, _data, context) => invalidateTemplates(context),
    })
    const { mutateAsync: callUpdateTemplate, isLoading: isUpdatingTemplate } = useApiMutation(updateMenuTemplate, {
        onSuccess: (_params, _data, context) => invalidateTemplates(context),
    })
    const isSavingTemplate = isCreatingTemplate || isUpdatingTemplate

    const requiredMessage = l10n.getString("merchant-menu-templates-validation-required")
    const numberMessage = l10n.getString("merchant-menu-templates-validation-number")
    const validationSchema = useMemo(
        () =>
            Yup.object({
                name: Yup.string().trim().required(requiredMessage),
                categories: Yup.array().of(
                    Yup.object({
                        name: Yup.string().trim().required(requiredMessage),
                        dishes: Yup.array().of(buildDishValidationSchema(requiredMessage, numberMessage)),
                    }),
                ),
            }),
        [requiredMessage, numberMessage],
    )

    const templatesListUrl = MERCHANT_ADMIN_SETTINGS_MENU_PLANNING_ROUTE
    const goToTemplatesList = () => navigate(templatesListUrl)

    const handleSubmit = async (values: MenuTemplateFormValues) => {
        const template = toTemplatePayload(values)
        const data = templateId
            ? await callUpdateTemplate({ template, templateId })
            : await callCreateTemplate({ template, merchantId })
        enqueueSnackbar({ variant: "success", message: data.serviceResponse.displayMessage })
        goToTemplatesList()
    }

    const isLoading = allergensQuery.isInitialLoading || (isEdit && detailsQuery.isInitialLoading)
    // Editing a template whose details failed to load must not fall back to an empty "new template" form.
    // Without the allergen list the saved allergen defaults cannot be resolved, so saving would drop them.
    const isLoadFailed = allergensQuery.isError || (isEdit && !detailsQuery.isInitialLoading && !detailsQuery.data)
    const initialValues =
        isEdit && detailsQuery.data
            ? toTemplateFormValues(detailsQuery.data, allergensQuery.data ?? [])
            : emptyTemplateFormValues()

    const pageTitle = isEdit
        ? (detailsQuery.data?.name ?? "")
        : l10n.getString("merchant-menu-templates-editor-new-title")

    const templateFormActions = (
        <Stack direction="row" spacing=".75rem">
            <LocalizedStrict id="merchant-menu-templates-editor-cancel-button">
                <StandardButton fullWidth={false} size="large" variant="outlined" onClick={goToTemplatesList}>
                    Cancel
                </StandardButton>
            </LocalizedStrict>
            <LocalizedStrict id="merchant-menu-templates-editor-save-button">
                <StandardLoadingButton
                    fullWidth={false}
                    size="large"
                    color="black"
                    disabled={templateFormState.submitCount > 0 && !templateFormState.isValid}
                    loading={isSavingTemplate}
                    onClick={() => templateFormRef.current?.submitForm()}
                >
                    Save template
                </StandardLoadingButton>
            </LocalizedStrict>
        </Stack>
    )

    // The dish drill-in is its own Formik, mounted below the header, so its submit is triggered through the ref.
    const dishFormActions = (
        <Stack direction="row" spacing=".75rem">
            <LocalizedStrict id="merchant-menu-templates-dish-cancel-button">
                <StandardButton fullWidth={false} size="large" variant="outlined" onClick={requestCloseDishEditor}>
                    Cancel
                </StandardButton>
            </LocalizedStrict>
            <LocalizedStrict id="merchant-menu-templates-dish-done-button">
                <StandardButton
                    fullWidth={false}
                    size="large"
                    color="black"
                    disabled={dishFormState.submitCount > 0 && !dishFormState.isValid}
                    onClick={() => dishFormRef.current?.submitForm()}
                >
                    Done
                </StandardButton>
            </LocalizedStrict>
        </Stack>
    )

    const renderPageHeader = (dishTitle?: string, onTemplateCrumbClick?: () => void, rightElement?: JSX.Element) => (
        <PageHeader
            page={Page.MENU_PLANNING}
            title={dishTitle ?? (pageTitle || l10n.getString("merchant-nav-item-menu-planning"))}
            rightElement={rightElement}
        >
            <StandardBreadcrumbs>
                <LocalizedStrict id="merchant-nav-item-home">
                    <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                </LocalizedStrict>
                <LocalizedStrict id="merchant-nav-item-settings-header">
                    <Breadcrumb>Settings</Breadcrumb>
                </LocalizedStrict>
                <LocalizedStrict id="merchant-nav-item-menu-planning">
                    <BreadcrumbLink to={templatesListUrl}>Menu planning</BreadcrumbLink>
                </LocalizedStrict>
                {dishTitle ? (
                    <Link
                        component="button"
                        type="button"
                        variant="caption"
                        className={classes.templateCrumbLink}
                        onClick={() => onTemplateCrumbClick?.()}
                    >
                        {pageTitle}
                    </Link>
                ) : (
                    pageTitle && <Breadcrumb>{pageTitle}</Breadcrumb>
                )}
                {dishTitle && <Breadcrumb>{dishTitle}</Breadcrumb>}
            </StandardBreadcrumbs>
        </PageHeader>
    )

    return (
        <>
            {isLoading || isLoadFailed ? (
                <>
                    {renderPageHeader()}
                    {isLoading ? (
                        <Stack spacing="1rem">
                            <Skeleton variant="rectangular" className={classes.sectionSkeleton} />
                            {Array.from({ length: SKELETON_CARDS }).map((_, index) => (
                                <Skeleton key={index} variant="rectangular" className={classes.cardSkeleton} />
                            ))}
                        </Stack>
                    ) : (
                        <StandardNoResultText noResultText={l10n.getString("merchant-menu-templates-load-error")} />
                    )}
                </>
            ) : (
                <Formik<MenuTemplateFormValues>
                    innerRef={templateFormRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, isValid, submitCount }) => {
                        // The Save template button lives in the page header, outside this Formik.
                        useEffect(() => {
                            setTemplateFormState({ isValid, submitCount })
                        }, [isValid, submitCount])

                        const openCategoryDialog = (categoryIndex: number | null) =>
                            setCategoryDialog({ open: true, categoryIndex })

                        const handleCategorySave = (name: string) => {
                            if (categoryDialog.categoryIndex === null) {
                                setFieldValue("categories", [...values.categories, { name, dishes: [] }])
                            } else {
                                setFieldValue(`categories[${categoryDialog.categoryIndex}].name`, name)
                            }
                            setCategoryDialog({ open: false, categoryIndex: null })
                        }

                        const handleRemoveCategory = (categoryIndex: number) =>
                            setDeleteTarget({ kind: "category", categoryIndex })

                        const handleAddDish = (categoryIndex: number) =>
                            setEditingDish({
                                categoryIndex,
                                dishIndex: null,
                                initialValues: defaultDishFormValues(),
                            })

                        const handleOpenDish = (categoryIndex: number, dishIndex: number) =>
                            setEditingDish({
                                categoryIndex,
                                dishIndex,
                                initialValues: values.categories[categoryIndex].dishes[dishIndex],
                            })

                        const handleRemoveDish = (categoryIndex: number, dishIndex: number) =>
                            setDeleteTarget({ kind: "dish", categoryIndex, dishIndex })

                        const handleDeleteConfirm = () => {
                            if (!deleteTarget) return
                            if (deleteTarget.kind === "category") {
                                setFieldValue(
                                    "categories",
                                    values.categories.filter((_, index) => index !== deleteTarget.categoryIndex),
                                )
                            } else {
                                setFieldValue(
                                    `categories[${deleteTarget.categoryIndex}].dishes`,
                                    values.categories[deleteTarget.categoryIndex].dishes.filter(
                                        (_, index) => index !== deleteTarget.dishIndex,
                                    ),
                                )
                            }
                        }

                        const deleteTargetCategory = deleteTarget
                            ? values.categories[deleteTarget.categoryIndex]
                            : undefined
                        const deleteTargetDishName =
                            deleteTarget?.kind === "dish"
                                ? deleteTargetCategory?.dishes[deleteTarget.dishIndex]?.name
                                : undefined

                        const handleDishDone = (dish: DishFormValues) => {
                            if (!editingDish) return
                            const { categoryIndex, dishIndex } = editingDish
                            const dishes = values.categories[categoryIndex].dishes
                            setFieldValue(
                                `categories[${categoryIndex}].dishes`,
                                dishIndex === null
                                    ? [...dishes, dish]
                                    : dishes.map((existing, index) => (index === dishIndex ? dish : existing)),
                            )
                            setEditingDish(null)
                        }

                        // From the opening values, so typing the dish name doesn't live-update the title.
                        const dishTitle = editingDish
                            ? editingDish.initialValues.name || l10n.getString("merchant-menu-templates-dish-new-title")
                            : undefined

                        return (
                            <>
                                {renderPageHeader(
                                    dishTitle,
                                    requestCloseDishEditor,
                                    editingDish ? dishFormActions : templateFormActions,
                                )}
                                {editingDish ? (
                                    <DishEditor
                                        innerRef={dishFormRef}
                                        initialValues={editingDish.initialValues}
                                        onDone={handleDishDone}
                                        onStateChange={handleDishFormStateChange}
                                    />
                                ) : (
                                    <StandardFormikErrorScrollWrapper<MenuTemplateFormValues>>
                                        <TemplateStructureForm
                                            onEditCategoryName={openCategoryDialog}
                                            onRemoveCategory={handleRemoveCategory}
                                            onAddDish={handleAddDish}
                                            onOpenDish={handleOpenDish}
                                            onRemoveDish={handleRemoveDish}
                                        />
                                    </StandardFormikErrorScrollWrapper>
                                )}

                                {deleteTarget && deleteTargetCategory && (
                                    <ConfirmModal
                                        open
                                        onClose={() => setDeleteTarget(null)}
                                        title={
                                            deleteTarget.kind === "category" ? (
                                                <LocalizedStrict id="merchant-menu-templates-delete-category-dialog-title">
                                                    <span>Delete category</span>
                                                </LocalizedStrict>
                                            ) : (
                                                <LocalizedStrict id="merchant-menu-templates-delete-dish-dialog-title">
                                                    <span>Delete dish</span>
                                                </LocalizedStrict>
                                            )
                                        }
                                        message={
                                            deleteTarget.kind === "category" ? (
                                                <Stack spacing=".25rem">
                                                    <LocalizedStrict
                                                        id="merchant-menu-templates-delete-category-dialog-message"
                                                        vars={{ name: deleteTargetCategory.name }}
                                                    >
                                                        <Typography>
                                                            Are you sure you want to delete "{deleteTargetCategory.name}
                                                            "?
                                                        </Typography>
                                                    </LocalizedStrict>
                                                    {deleteTargetCategory.dishes.length > 0 && (
                                                        <LocalizedStrict
                                                            id="merchant-menu-templates-delete-category-dialog-dishes-warning"
                                                            vars={{ count: deleteTargetCategory.dishes.length }}
                                                        >
                                                            <Typography>
                                                                It contains {deleteTargetCategory.dishes.length} dishes,
                                                                which will be deleted too.
                                                            </Typography>
                                                        </LocalizedStrict>
                                                    )}
                                                </Stack>
                                            ) : deleteTargetDishName ? (
                                                <LocalizedStrict
                                                    id="merchant-menu-templates-delete-dish-dialog-message"
                                                    vars={{ name: deleteTargetDishName }}
                                                >
                                                    <Typography>
                                                        Are you sure you want to delete "{deleteTargetDishName}"?
                                                    </Typography>
                                                </LocalizedStrict>
                                            ) : (
                                                <LocalizedStrict id="merchant-menu-templates-delete-dish-dialog-message-unnamed">
                                                    <Typography>Are you sure you want to delete this dish?</Typography>
                                                </LocalizedStrict>
                                            )
                                        }
                                        handleClick={handleDeleteConfirm}
                                        buttonLabelSubmit={l10n.getString("delete-confirm-modal-button-delete")}
                                    />
                                )}

                                {categoryDialog.open && (
                                    <CategoryNameDialog
                                        open={categoryDialog.open}
                                        isNew={categoryDialog.categoryIndex === null}
                                        initialName={
                                            categoryDialog.categoryIndex !== null
                                                ? values.categories[categoryDialog.categoryIndex].name
                                                : ""
                                        }
                                        onSave={handleCategorySave}
                                        onClose={() => setCategoryDialog({ open: false, categoryIndex: null })}
                                    />
                                )}

                                <CreateOrderCloseConfirmDialog
                                    open={isDiscardConfirmOpen}
                                    onClose={() => setDiscardConfirmOpen(false)}
                                    onDiscard={handleDiscardDish}
                                />
                            </>
                        )
                    }}
                </Formik>
            )}
        </>
    )
}
